import { useApi } from '@/api';
import { Asset, Link, ListSkeleton } from '@/components';
import {
  Badge,
  Card,
  CurrencyValue,
  EmptyBox,
  Flex,
  List,
  ListHeader,
  ListItem
} from '@blockanalitica/ui';
import { useParams } from 'react-router';
import { useTheme } from 'styled-components';
import { Wallet } from '.';
import { DEFAULT_NETWORK } from '@/constants';

type Position = {
  underlying_address: string;
  supply: string;
  supply_usd: string;
  price: string;
  underlying_symbol: string;
  tags: (string | null)[];
};

export default function SupplyPositionsCardList({
  dataTotal
}: {
  dataTotal: Wallet;
}) {
  const theme = useTheme();
  const { walletAddress } = useParams() as {
    walletAddress: string;
  };
  const network = DEFAULT_NETWORK;
  const [data, error] = useApi<Position[]>({
    url: `/wallets/${walletAddress}/positions/supply/`
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <ListSkeleton />;
  }

  return (
    <Card>
      <List>
        <ListHeader
          title="Supplied"
          actions={[
            <Flex
              fontSize="1"
              flexDirection="column"
              gap="2xs"
              alignItems="flex-end"
              key={0}>
              <CurrencyValue
                value={dataTotal.total_supply}
                options={{ currency: 'USD' }}
              />
              <CurrencyValue
                fontSize="-1"
                value={dataTotal.total_supply_change}
                options={{
                  currency: 'USD',
                  trend: true,
                  trendIconSize: '-2'
                }}
              />
            </Flex>
          ]}
        />

        {data.length > 0 ? (
          data.map((row) => (
            <ListItem
              // @ts-ignore
              as={Link}
              key={row.underlying_address}
              to={`/markets/${row.underlying_address}/wallets/${walletAddress}?tab=supply`}
              relative="path">
              <Flex justifyContent="space-between">
                <Asset
                  symbol={row.underlying_symbol}
                  address={row.underlying_address}
                  network={network}
                  name={
                    <CurrencyValue
                      value={row.price}
                      options={{ currency: 'USD' }}
                    />
                  }
                  iconSize="3"
                />
                <Flex
                  flexDirection="row"
                  alignItems="flex-start"
                  gap={['2xs', '3xs']}>
                  {row.tags
                    .filter((badge: string | null) => badge != null)
                    .map((badge: string | null, index: number) => (
                      <Badge key={index} variant="marketTag">
                        {badge}
                      </Badge>
                    ))}
                </Flex>
                <Flex flexDirection="column" gap={['2xs', '3xs']}>
                  <Flex justifyContent="flex-end">
                    <CurrencyValue
                      value={row.supply_usd}
                      options={{
                        currency: 'USD'
                      }}
                    />
                  </Flex>
                  <Flex justifyContent="flex-end" fontSize="-1">
                    <CurrencyValue
                      value={row.supply}
                      color={theme.colors.muted}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </ListItem>
          ))
        ) : (
          <EmptyBox message="No supply positions" />
        )}
      </List>
    </Card>
  );
}
